import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, FileText, Languages, Loader2, Save, ShieldCheck } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

type LegalContent = {
  no: string;
  en: string;
};

type AdminPrivacyPanelProps = {
  privacyContent: LegalContent;
  onPrivacyChange: (field: keyof LegalContent, value: string) => void;
  onSavePrivacyPolicy: () => void;
  termsContent: LegalContent;
  onTermsChange: (field: keyof LegalContent, value: string) => void;
  onSaveTerms: () => void;
  processingId: string | null;
  isPrivacySaved: boolean;
  isTermsSaved: boolean;
};

const AdminPrivacyPanel: React.FC<AdminPrivacyPanelProps> = ({
  privacyContent,
  onPrivacyChange,
  onSavePrivacyPolicy,
  termsContent,
  onTermsChange,
  onSaveTerms,
  processingId,
  isPrivacySaved,
  isTermsSaved
}) => {
  const { t } = useTranslation();
  const [activeSubTab, setActiveSubTab] = useState<'privacy' | 'terms'>('privacy');
  const [isTranslating, setIsTranslating] = useState(false);

  const handleTranslateToEnglish = async (
    norwegianText: string,
    onUpdate: (field: keyof LegalContent, value: string) => void
  ) => {
    if (!norwegianText.trim()) return;
    setIsTranslating(true);
    try {
      const fns = getFunctions(undefined, 'us-central1');
      const translateFn = httpsCallable<{ text: string; targetLang: string }, { ok: boolean; translated: string }>(fns, 'translateLegalText');
      const result = await translateFn({ text: norwegianText, targetLang: 'en' });
      if (result.data?.ok && result.data.translated) {
        onUpdate('en', result.data.translated);
      }
    } catch (err) {
      console.error('Translation failed:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  const TranslateButton = ({ norwegianText, onUpdate }: { norwegianText: string; onUpdate: (field: keyof LegalContent, value: string) => void }) => (
    <button
      onClick={() => handleTranslateToEnglish(norwegianText, onUpdate)}
      disabled={isTranslating || !norwegianText.trim()}
      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest bg-blue-500/20 border border-blue-500/30 text-blue-400 hover:bg-blue-500/30 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {isTranslating ? <Loader2 size={14} className="animate-spin" /> : <Languages size={14} />}
      {isTranslating ? 'Oversetter med AI...' : 'Oversett norsk til engelsk'}
    </button>
  );

  return (
    <div className="space-y-4 animate-in slide-in-from-bottom-4">

      {/* Sub-tabs */}
      <div className="flex items-center gap-1.5 p-1 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl w-fit">
        <button
          onClick={() => setActiveSubTab('privacy')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'privacy'
            ? 'bg-[var(--bg-input)] text-[var(--text-primary)] shadow-xl shadow-black/20'
            : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          <ShieldCheck size={12} />
          {t('admin.privacy_title', 'Personvern')}
        </button>
        <button
          onClick={() => setActiveSubTab('terms')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'terms'
            ? 'bg-[var(--bg-input)] text-[var(--text-primary)] shadow-xl shadow-black/20'
            : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          <FileText size={12} />
          {t('admin.terms_title', 'Brukervilkår')}
        </button>
      </div>

      {activeSubTab === 'privacy' ? (
        <div className="bg-emerald-500/5 border border-emerald-500/20 p-4 md:p-6 rounded-panel space-y-4">
          <div className="flex items-center gap-3 text-emerald-500">
            <ShieldCheck size={20} />
            <div>
              <h3 className="text-tiny font-black uppercase tracking-widest">{t('admin.privacy_title', 'Personvernserklæring')}</h3>
              <p className="text-3xs font-bold opacity-70">{t('admin.privacy_subtitle', 'Vedlikehold tekst for appens personvern')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1">Norsk (NB)</label>
              <textarea
                value={privacyContent.no}
                onChange={(e) => onPrivacyChange('no', e.target.value)}
                placeholder={t('admin.privacy_placeholder_no', 'Skriv personvernserklæringen her...')}
                className="w-full h-[50vh] bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl p-4 text-tiny text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500 resize-none leading-relaxed"
              />
            </div>
            <div className="space-y-2">
              <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1">English (EN)</label>
              <TranslateButton norwegianText={privacyContent.no} onUpdate={onPrivacyChange} />
              <textarea
                value={privacyContent.en}
                onChange={(e) => onPrivacyChange('en', e.target.value)}
                placeholder={t('admin.privacy_placeholder_en', 'Write the privacy policy in english here...')}
                className="w-full h-[50vh] bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl p-4 text-tiny text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500 resize-none leading-relaxed"
              />
            </div>
          </div>

          <button
            onClick={onSavePrivacyPolicy}
            disabled={processingId === 'privacy-save'}
            className={`w-full py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 ${isPrivacySaved ? 'bg-emerald-600 text-white' : 'bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-600/30'}`}
          >
            {processingId === 'privacy-save' ? <Loader2 size={16} className="animate-spin" /> : isPrivacySaved ? <CheckCircle size={16} /> : <Save size={16} />}
            {isPrivacySaved ? t('admin.privacy.save_success') : t('admin.privacy_save_btn', 'Lagre endringer')}
          </button>
        </div>
      ) : (
        <div className="bg-amber-500/5 border border-amber-500/20 p-4 md:p-6 rounded-panel space-y-4">
          <div className="flex items-center gap-3 text-amber-500">
            <FileText size={20} />
            <div>
              <h3 className="text-tiny font-black uppercase tracking-widest">{t('admin.terms_title', 'Brukervilkår')}</h3>
              <p className="text-3xs font-bold opacity-70">{t('admin.terms_subtitle', 'Vedlikehold tekst for appens brukervilkår')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1">Norsk (NB)</label>
              <textarea
                value={termsContent.no}
                onChange={(e) => onTermsChange('no', e.target.value)}
                placeholder="Skriv brukervilkårene her..."
                className="w-full h-[50vh] bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl p-4 text-tiny text-[var(--text-secondary)] focus:outline-none focus:border-amber-500 resize-none leading-relaxed"
              />
            </div>
            <div className="space-y-2">
              <label className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest px-1">English (EN)</label>
              <TranslateButton norwegianText={termsContent.no} onUpdate={onTermsChange} />
              <textarea
                value={termsContent.en}
                onChange={(e) => onTermsChange('en', e.target.value)}
                placeholder="Write the terms of service in english here..."
                className="w-full h-[50vh] bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-2xl p-4 text-tiny text-[var(--text-secondary)] focus:outline-none focus:border-amber-500 resize-none leading-relaxed"
              />
            </div>
          </div>

          <button
            onClick={onSaveTerms}
            disabled={processingId === 'terms-save'}
            className={`w-full py-4 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95 ${isTermsSaved ? 'bg-amber-600 text-white' : 'bg-amber-600/20 border border-amber-500/30 text-amber-300 hover:bg-amber-600/30'}`}
          >
            {processingId === 'terms-save' ? <Loader2 size={16} className="animate-spin" /> : isTermsSaved ? <CheckCircle size={16} /> : <Save size={16} />}
            {isTermsSaved ? t('admin.terms.save_success', 'Lagret!') : t('admin.terms_save_btn', 'Lagre brukervilkår')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminPrivacyPanel;
